import React, { useEffect, useState, useMemo, useCallback } from 'react'
import {
  useLazyQuery,
  useMutation,
  useQuery,
  useReactiveVar,
} from '@apollo/client'
import { useHistory } from 'react-router-dom'
import moment from 'moment'
import _ from 'lodash'

import { currentUserDetails } from '../api/apollo/cache/variables'
import {
  getLostLinksFilterOptions,
  getLostLinksGraphData,
  getLostLinksTableData,
  listSavedLostLinksReports,
  saveNewLostLinkReport,
  updateSavedLostLinkReport,
} from '../api/apollo/graphql/report-client'
import { getAccountDataSource } from '../api/apollo/graphql/workspace-client'
import { DimensionOption, SelectedDimensions } from '../api/types'
import CollapsableFilters from '../components/collapsable-filters'
import Input from '../components/input'
import Intro from '../components/intro'
import Layout from '../components/layout'
import LostLinksReportHeader from '../components/lost-links-report-header'
import LostLinksDataCallout from '../components/lost-links-data-callout'
import LostLinksDataSummary from '../components/lost-links-data-summary'
import LostLinksActionButtons from '../components/lost-links-action-buttons'
import LostLinksGraph from '../components/lost-links-graph'
import LostLinksSearchBox from '../components/lost-links-search-box'
import ManageLostLinkReportsTabs, {
  ManageSavedReportsModal,
  SaveEditReportModal,
} from '../components/lost-links-manage-reports'
import LostLinksTable, { PaginationData } from '../components/lost-links-table'
import SiteWrapper from '../components/site-wrapper'
import { AvailableDimension } from '../context/reducers/graphql-reducer'
import { dateFormatShort, performanceDates } from '../core/variables'
import { getItemByKeyValue, getUrlQuery } from '../helpers'
import { metricNameMapper, paramNameMapper } from '../helpers/report-module'
import useLogAction from '../hooks/useLogAction'
import useResize from '../hooks/useResize'
import styles from '../styles/lost-links-report.module.scss'

const yesterday = new Date(Date.now())
yesterday.setDate(yesterday.getDate() - 1)

const initialPaginationConfig: PaginationConfig = {
  limit: 10,
  offset: 1,
  sortDirection: 'DESC',
}

const initialDataConfig: DataConfig = {
  startDate: 'thisYear',
  endDate: undefined,
  selectedMetric: 'sessions',
  granularity: 'monthly',
  filterList: undefined,
  searchFilter: undefined,
}

export default function LostLinksReportPage() {
  const { workspaceID } = useReactiveVar(currentUserDetails)

  const logAction = useLogAction()

  const history = useHistory()

  const [width, setWidth] = useState(0)
  const [dataConfig, setDataConfig] = useState<DataConfig>(initialDataConfig)
  const [currentReport, setCurrentReport] = useState<SavedReport | null>(null)
  const [reportToEdit, setReportToEdit] = useState<SavedReport | null>(null)
  const [urlReportIDCheckComplete, setUrlReportIDCheckComplete] = useState<
    'default' | 'saved' | false
  >(false)
  const [filtersHaveChanged, setFiltersHaveChanged] = useState(false)

  const [paginationData, setPaginationData] = useState<PaginationData | null>(
    null,
  )
  const [tempTables, setTempTables] = useState<{
    [tempTableID: string]: DataConfigWithTotalRows
  }>({})
  const [currentTempTable, setCurrentTempTable] = useState<string | null>(null)
  const [totalTableRows, setTotalTableRows] = useState(0)
  const [showSaveReportModal, setShowSaveReportModal] = useState(false)
  const [showEditReportsModal, setShowEditReportsModal] = useState(false)
  const [
    groupTableDataByLandingPage,
    setGroupTableDataByLandingPage,
  ] = useState(false)

  const screenWidth = useResize()

  const isMobile = screenWidth <= 768
  let offset =
    dataConfig.granularity === 'monthly' ||
    dataConfig.granularity === 'quarterly'
      ? 0
      : 40
  if (isMobile && offset > 0) offset = 20

  const { data: dataSourceData } = useQuery(getAccountDataSource)

  const {
    data: savedReportsData,
    loading: loadingSavedReports,
    error: errorFetchingSavedReports,
  } = useQuery(listSavedLostLinksReports, { fetchPolicy: 'cache-first' })
  const [
    getGraphData,
    { data: fullGraphData, loading: graphDataLoading, error: graphDataError },
  ] = useLazyQuery(getLostLinksGraphData, {
    notifyOnNetworkStatusChange: true,
  })
  const [
    getTableData,
    { data: tableData, loading: tableDataLoading, error: tableDataError },
  ] = useLazyQuery(getLostLinksTableData, {
    notifyOnNetworkStatusChange: true,
  })
  const [
    getFilterOptions,
    { data: filtersData, loading: loadingFilters },
  ] = useLazyQuery(getLostLinksFilterOptions, {
    notifyOnNetworkStatusChange: true,
  })

  const [saveReport, { loading: savingNewReport }] = useMutation(
    saveNewLostLinkReport,
    {
      refetchQueries: [listSavedLostLinksReports],
    },
  )
  const [updateSavedReport] = useMutation(updateSavedLostLinkReport, {
    refetchQueries: [listSavedLostLinksReports],
  })

  const isFilteredReport = !(currentReport === null && !dataConfig.filterList)

  // Only GA accounts should see this page
  useEffect(() => {
    if (!dataSourceData || !dataSourceData.currentAccount.dataSource) return

    if (
      ['GA_VIEW', 'GA4_PROP'].indexOf(
        dataSourceData.currentAccount.dataSource.kind,
      ) === -1
    ) {
      history.push('/connect')
    }
  }, [dataSourceData])

  const {
    graphData,
    dateRange: graphDateRange,
    dateTotalRows: graphTableTotals,
    queryStartDate,
    queryEndDate,
    total,
  } = useMemo(() => {
    if (!fullGraphData) {
      return {
        graphData: null,
        dateRange: null,
        dateTotalRows: null,
        queryStartDate: null,
        queryEndDate: null,
        total: null,
      }
    }

    if (!isFilteredReport) {
      return fullGraphData.report.linkAudit.linkAuditGraph
    }

    const nonUplifterLinks = fullGraphData.report.linkAudit.linkAuditGraph.graphData.find(
      (data) => data.dimensionName === 'Total not in uplifter',
    )

    // The 'Other links sessions'/'Uplifter link sessions' breakdown should not show when the report is filtered
    return {
      ...fullGraphData.report.linkAudit.linkAuditGraph,
      graphData: nonUplifterLinks ? [nonUplifterLinks] : [],
    }
  }, [fullGraphData, currentReport, dataConfig.filterList])

  const uplifterLinksTotal = useMemo(() => {
    if (!graphData || currentReport !== null || !!dataConfig.filterList)
      return null

    return graphData[1].dimensionValues.reduce((acc, curr) => acc + curr, 0)
  }, [graphData, currentReport, dataConfig.filterList])

  const dataBasedDateRange = useMemo(() => {
    if (graphDataLoading || !queryStartDate || !queryEndDate) return ''

    const start = moment(queryStartDate, 'YYYY-MM-DD').format(dateFormatShort)
    const end = moment(queryEndDate, 'YYYY-MM-DD').format(dateFormatShort)

    return `${start} - ${end}`
  }, [graphDataLoading, queryStartDate, queryEndDate])

  const dateRangeLabel = useMemo(() => {
    const { startDate, endDate } = dataConfig

    if (!startDate) return ''

    const currentDateItem = getItemByKeyValue(
      performanceDates,
      'metricValue',
      startDate,
    )

    if (
      currentDateItem &&
      currentDateItem.label &&
      currentDateItem.metricValue
    ) {
      return ` over ${currentDateItem.label}`
    }

    const from = moment(startDate).format(dateFormatShort)

    if (!endDate) return `from ${from}`

    const to = moment(endDate).format(dateFormatShort)

    return ` from ${from} to ${to}`
  }, [dataConfig.startDate, dataConfig.endDate])

  // Update table row count
  useEffect(() => {
    if (
      currentTempTable &&
      tempTables[currentTempTable] &&
      typeof tempTables[currentTempTable].totalRows === 'number'
    ) {
      setTotalTableRows(tempTables[currentTempTable].totalRows as number)
      return
    }

    if (!tableData || !tableData.report.linkAudit.linkAuditTable.pageData)
      return

    setTotalTableRows(tableData.report.linkAudit.linkAuditTable.pageData.total)
  }, [tableData, tempTables, currentTempTable])

  // Get saved reports
  const { savedReports, userPreferredOrder } = useMemo(() => {
    if (!savedReportsData) {
      return {
        savedReports: [],
        userPreferredOrder: [] as string[],
      }
    }

    return {
      ...savedReportsData.report.listSavedLinkAuditReports,
      savedReports: savedReportsData.report.listSavedLinkAuditReports.savedReports.map(
        (report) => {
          return {
            ...report,
            applyFilters:
              report.applyFilters?.filter(
                (aF) => aF.dimensionOptions.length > 0,
              ) || [],
          }
        },
      ),
    }
  }, [savedReportsData])

  // Check if URL has a report ID that we should filter to
  useEffect(() => {
    if (!workspaceID) return

    const urlParams = getUrlQuery()

    const reportID = urlParams.get('reportID')

    // No need to set current report - allow data to load
    if (!urlParams || !reportID) {
      setUrlReportIDCheckComplete('default')
      return
    }

    // Only allow report data to be fetched once saved reports are found
    if (savedReportsData || errorFetchingSavedReports) {
      const foundSavedReport = savedReportsData?.report.listSavedLinkAuditReports.savedReports.find(
        ({ savedReportID }) => reportID === savedReportID,
      )

      if (foundSavedReport) {
        const {
          applyFilters,
          startDate,
          endDate,
          selectedMetric,
          granularity,
        } = foundSavedReport

        setDataConfig({
          startDate,
          endDate,
          selectedMetric,
          granularity: (granularity as Granularity) || 'monthly',
          filterList: applyFilters
            ? {
                dimensionFilters: applyFilters.map(
                  ({ dimensionOptions, dimensionParameterID }) => ({
                    dimensionName: dimensionParameterID,
                    dimensionOptions,
                    dimensionParameterID,
                  }),
                ),
              }
            : undefined,
          searchFilter: undefined,
        })

        setCurrentReport(foundSavedReport as SavedReport)
      }

      setUrlReportIDCheckComplete(foundSavedReport ? 'saved' : 'default')
    }
  }, [workspaceID, savedReportsData, errorFetchingSavedReports])

  // Initial fetch
  useEffect(() => {
    const initialFetch = async (useDefault = true) => {
      if (
        !workspaceID ||
        !dataSourceData ||
        !dataSourceData.currentAccount.dataSource
      ) {
        return
      }

      getGraphData({
        variables: {
          ...dataConfig,
          selectedMetric: `${
            dataSourceData?.currentAccount.dataSource?.kind === 'GA_VIEW'
              ? 'ga:'
              : ''
          }${dataConfig.selectedMetric}`,
        },
      })

      const { data: newTableData } = await getTableData({
        variables: {
          ...dataConfig,
          ...initialPaginationConfig,
          selectedMetric: `${
            dataSourceData.currentAccount.dataSource.kind === 'GA_VIEW'
              ? 'ga:'
              : ''
          }${dataConfig.selectedMetric}`,
          groupByLandingPage: undefined,
          tempTable: undefined,
        },
        fetchPolicy: 'network-only',
      })

      if (
        newTableData &&
        newTableData.report.linkAudit.linkAuditTable.pageData?.destination
      ) {
        // Refetch filters when tempTable changes
        getFilterOptions({
          variables: {
            tempTable: useDefault
              ? newTableData.report.linkAudit.linkAuditTable.pageData
                  .destination
              : undefined,
          },
        })
      }
    }

    // If URL has a reportID, mustn't fetch data till saved reports are available
    if (urlReportIDCheckComplete) {
      initialFetch(urlReportIDCheckComplete === 'default')
    }
  }, [workspaceID, dataSourceData, urlReportIDCheckComplete])

  const refetchData = useCallback(
    async (
      data: DataConfig,
      {
        tempTable,
        pagination,
        refetchTable,
        refetchGraph,
        refetchFilters,
        groupByLandingPage,
      }: RefetchOptions = { refetchFilters: true },
    ) => {
      if (refetchGraph !== false) {
        getGraphData({
          variables: {
            ...data,
            selectedMetric: `${
              dataSourceData?.currentAccount.dataSource?.kind === 'GA_VIEW'
                ? 'ga:'
                : ''
            }${dataConfig.selectedMetric}`,
          },
        })
      }

      if (refetchTable !== false) {
        // Only use tempTable if pagination is changing
        if (pagination) {
          let tempTableToUse: string | null = tempTable || null

          if (!tempTableToUse) {
            // Check if requested dataConfig exists on existing tempTables
            // Must also include totalRows, since pageData object is not returned on tempTables
            Object.entries(tempTables).forEach(([key, value]) => {
              if (_.isEqual(value, { ...data, totalRows: value.totalRows })) {
                tempTableToUse = key
              }
            })
          }

          // If requested dataConfig matches existing tempTable config
          if (tempTableToUse) {
            getTableData({
              variables: {
                tempTable: tempTableToUse,
                ...pagination,
              },
            })

            return
          }
        }

        const { data: newTableData } = await getTableData({
          variables: {
            ...data,
            ...initialPaginationConfig,
            selectedMetric: `${
              dataSourceData?.currentAccount.dataSource?.kind === 'GA_VIEW'
                ? 'ga:'
                : ''
            }${data.selectedMetric}`,
            groupByLandingPage,
            tempTable: undefined,
          },
        })

        if (
          refetchFilters &&
          newTableData &&
          newTableData.report.linkAudit.linkAuditTable.pageData?.destination
        ) {
          // Refetch filters when tempTable changes
          getFilterOptions({
            variables: {
              tempTable:
                newTableData.report.linkAudit.linkAuditTable.pageData
                  .destination,
            },
          })
        }
      }
    },
    [workspaceID, tempTables, dataSourceData, currentReport, dataConfig],
  )

  // Rerun the report when a new one is fetched while being on the same page
  useEffect(() => {
    // Should only run if URL has changed but page is the same
    if (!urlReportIDCheckComplete) return

    const urlParams = getUrlQuery()

    const reportID = urlParams.get('reportID')

    // No need to set current report - allow data to load
    if (!urlParams || !reportID) {
      setCurrentReport(null)
      setDataConfig(initialDataConfig)

      refetchData(initialDataConfig, { refetchFilters: false })
      return
    }

    const foundSavedReport = savedReportsData?.report.listSavedLinkAuditReports.savedReports.find(
      ({ savedReportID }) => reportID === savedReportID,
    )

    if (foundSavedReport) {
      const {
        applyFilters,
        startDate,
        endDate,
        selectedMetric,
        granularity,
      } = foundSavedReport

      const newDataConfig = {
        startDate,
        endDate,
        selectedMetric,
        granularity: (granularity as Granularity) || 'monthly',
        filterList: applyFilters
          ? {
              dimensionFilters: applyFilters.map(
                ({ dimensionOptions, dimensionParameterID }) => ({
                  dimensionName: dimensionParameterID,
                  dimensionOptions,
                  dimensionParameterID,
                }),
              ),
            }
          : undefined,
        searchFilter: undefined,
      }

      setDataConfig(newDataConfig)

      refetchData(newDataConfig, { refetchFilters: false })

      setCurrentReport(foundSavedReport as SavedReport)
    }
  }, [window.location.search])

  // Update tempTables whenever dataConfig is changed
  useEffect(() => {
    if (!tableData || !tableData.report.linkAudit.linkAuditTable.pageData)
      return

    const newTempTable =
      tableData.report.linkAudit.linkAuditTable.pageData.destination
    const tempTableTotalRows =
      tableData.report.linkAudit.linkAuditTable.pageData.total

    if (newTempTable === currentTempTable) return

    setCurrentTempTable(newTempTable)

    setTempTables({
      ...tempTables,
      [newTempTable]: {
        ...dataConfig,
        totalRows: tempTableTotalRows,
      },
    })
  }, [tableData, currentTempTable, dataConfig])

  // Get dimensions for filter
  const availableDimensions: AvailableDimension[] = useMemo(() => {
    if (!filtersData) return []

    return filtersData.report.linkAudit.linkAuditFilters.parameterFilters.map(
      (param) => {
        return {
          dimensionParameterID: param.parameterID,
          dimensionName: paramNameMapper[param.parameterID],
          dimensionOptions: param.parameterOptions,
          parameterType: 'select',
        }
      },
    )
  }, [filtersData])

  // Update selected dimensions state
  const selectedDimensions: SelectedDimensions = useMemo(() => {
    if (!dataConfig.filterList) return {}

    const newSelectedDimensions = {}

    dataConfig.filterList.dimensionFilters.forEach(
      ({ dimensionName, dimensionParameterID, dimensionOptions }) => {
        newSelectedDimensions[paramNameMapper[dimensionName]] = {
          dimensionParameterID,
          dimensionOptions,
        }
      },
    )

    return { ...newSelectedDimensions }
  }, [dataConfig.filterList])

  const onChangeFilters = useCallback(
    async (val: DimensionOption, item: AvailableDimension) => {
      const newDataConfig = {
        ...dataConfig,
      }

      if (!dataConfig.filterList) {
        newDataConfig.filterList = {
          dimensionFilters: [
            { ...val, dimensionName: val.dimensionParameterID },
          ],
        }
      } else {
        const newFilterList = [...dataConfig.filterList.dimensionFilters]

        const dimensionFilterIndex = newFilterList.findIndex(
          (dim) => dim.dimensionParameterID === item.dimensionParameterID,
        )

        if (dimensionFilterIndex === -1) {
          newFilterList.push({
            ...val,
            dimensionName: val.dimensionParameterID,
          })
        } else {
          newFilterList.splice(dimensionFilterIndex, 1, {
            ...val,
            dimensionName: val.dimensionParameterID,
          })
        }

        newDataConfig.filterList = { dimensionFilters: newFilterList }
      }

      setDataConfig(newDataConfig)
      setFiltersHaveChanged(true)
      await refetchData(newDataConfig, { refetchFilters: false })

      logAction({
        variables: {
          action: 'update-filters-link-audit',
          extra: JSON.stringify(newDataConfig),
          websiteSection: 'report',
          functionName: 'onChangeFilters',
          pagePath: window.location.pathname,
        },
      })
    },
    [dataConfig],
  )

  const switchCurrentReport = useCallback(
    (resetToReport: SavedReport | null) => {
      setCurrentReport(resetToReport)

      if (resetToReport !== null) {
        const newDataConfig: DataConfig = {
          ...dataConfig,
          startDate: resetToReport.startDate,
          endDate: resetToReport.endDate,
          selectedMetric: resetToReport.selectedMetric,
          granularity: resetToReport.granularity || 'monthly',
          filterList: resetToReport.applyFilters
            ? {
                dimensionFilters: resetToReport.applyFilters.map(
                  ({ dimensionParameterID, dimensionOptions }) => ({
                    dimensionName: dimensionParameterID,
                    dimensionParameterID,
                    dimensionOptions,
                  }),
                ),
              }
            : undefined,
        }

        setDataConfig(newDataConfig)
        refetchData(newDataConfig, { refetchFilters: false })
      } else {
        const newDataConfig: DataConfig = {
          startDate: 'thisYear',
          endDate: undefined,
          selectedMetric: 'sessions',
          granularity: 'monthly' as Granularity,
          filterList: undefined,
          searchFilter: undefined,
        }

        setDataConfig(newDataConfig)
        refetchData(newDataConfig, { refetchFilters: false })
      }

      setFiltersHaveChanged(false)
    },
    [dataConfig],
  )

  const getNewReportConfig = useCallback(() => {
    const filterList = Object.keys(selectedDimensions).map((key) => {
      return {
        dimensionParameterID: selectedDimensions[key].dimensionParameterID,
        dimensionName: key,
        dimensionOptions: selectedDimensions[key].dimensionOptions,
      }
    })

    return {
      filterList,
      startDate: dataConfig.startDate || 'thisYear',
      endDate: dataConfig.endDate || '',
      selectedMetric: dataConfig.selectedMetric,
      granularity: dataConfig.granularity || 'monthly',
    }
  }, [selectedDimensions, dataConfig])

  return (
    <>
      <SiteWrapper>
        <Layout width={1600}>
          <div
            ref={(element) => {
              if (element) {
                const container = element.getBoundingClientRect()
                setWidth(container.width - 42)
              }
            }}
          >
            <Intro title="Other links" isBeta className={styles.intro}>
              <p>
                Discover landing pages with UTMs created by other platforms /
                users to plan integrations and additional training.
              </p>
              {errorFetchingSavedReports && (
                <p className={styles.errorMsg}>
                  Error retrieving saved reports.
                </p>
              )}
            </Intro>
            <CollapsableFilters
              accountID={workspaceID}
              isMobile={isMobile}
              data={availableDimensions}
              loading={loadingFilters}
              error={!!tableDataError}
              selectedDimensions={selectedDimensions}
              onResetFilters={async () => {
                const newDataConfig = {
                  ...dataConfig,
                  filterList: undefined,
                }

                setDataConfig(newDataConfig)
                setFiltersHaveChanged(false)
                await refetchData(newDataConfig, { refetchFilters: false })

                logAction({
                  variables: {
                    action: 'update-filters-link-audit',
                    extra: JSON.stringify(newDataConfig),
                    websiteSection: 'report',
                    functionName: 'onChangeFilters',
                    pagePath: window.location.pathname,
                  },
                })
              }}
              onChange={onChangeFilters}
            >
              <LostLinksDataSummary
                dataConfig={dataConfig}
                setDataConfig={setDataConfig}
                refetchData={refetchData}
                setFiltersHaveChanged={setFiltersHaveChanged}
              />
            </CollapsableFilters>
            <ManageLostLinkReportsTabs
              getNewReportConfig={getNewReportConfig}
              setShowSaveReportModal={setShowSaveReportModal}
              showSaveButton={filtersHaveChanged}
              loading={availableDimensions.length === 0}
              loadingSavedReports={loadingSavedReports}
              reportsOrder={userPreferredOrder}
              savedReports={savedReports}
              currentReport={currentReport}
              onResetReport={switchCurrentReport}
              onSaveReport={() => setFiltersHaveChanged(false)}
              updateSavedReport={updateSavedReport}
            >
              <LostLinksActionButtons
                dataConfig={dataConfig}
                getNewReportConfig={getNewReportConfig}
                pagination={paginationData}
                tempTable={currentTempTable}
                filtersHaveChanged={filtersHaveChanged}
                currentReport={currentReport}
                onSaveReport={() => setFiltersHaveChanged(false)}
                updateSavedReport={updateSavedReport}
                savedReports={savedReports}
                setShowSaveReportModal={setShowSaveReportModal}
                setShowEditReportsModal={setShowEditReportsModal}
              />
            </ManageLostLinkReportsTabs>
            <div className={styles.paper}>
              <LostLinksReportHeader
                dateRange={dataBasedDateRange}
                dataSourceData={dataSourceData}
                isMobile={isMobile}
                onChangeFilters={onChangeFilters}
                availableDimensions={availableDimensions}
                selectedDimensions={selectedDimensions}
                currentReport={currentReport}
              />
              <div className={styles.metricDataBannerWrapper}>
                <div className={styles.metricDataBanner}>
                  <LostLinksDataCallout
                    loading={!tableData || tableDataLoading}
                    error={!!tableDataError}
                    successMetric={isFilteredReport ? 'Links' : 'Other links'}
                    tooltip="Landing pages with UTMs found in Google Analytics which were not created in Uplifter."
                    metricValue={totalTableRows}
                    dateRange={dataBasedDateRange}
                  />
                  <LostLinksDataCallout
                    loading={!graphData || graphDataLoading}
                    error={!!graphDataError}
                    successMetric={
                      isFilteredReport ? 'Sessions' : 'Other link sessions'
                    }
                    tooltip="A group of user interactions that take place within a limited time frame (ends after 30mins, if this has not been adjusted in settings), from lost links."
                    metricValue={total || 0}
                    dateRange={dataBasedDateRange}
                  />
                  {/* Only show this callout if current report is not a saved one and no filters have been applied */}
                  {!isFilteredReport && (
                    <LostLinksDataCallout
                      loading={!graphData || graphDataLoading}
                      error={!!graphDataError}
                      successMetric="% sessions from other links"
                      tooltip="Other link sessions divided by all link sessions (Uplifter and non-uplifter links) found in Google Analytics."
                      metricValue={
                        total !== null && uplifterLinksTotal !== null
                          ? (total / (total + uplifterLinksTotal)) * 100
                          : 0
                      }
                      percentage
                      dateRange={dataBasedDateRange}
                    />
                  )}
                </div>
              </div>
              <div className={styles.graphWrapper}>
                <LostLinksGraph
                  width={width}
                  screenWidth={screenWidth}
                  offset={offset}
                  granularity={dataConfig.granularity || 'monthly'}
                  isMobile={isMobile}
                  graphDateRange={graphDateRange}
                  graphData={graphData}
                  graphTableTotals={graphTableTotals}
                  stackDimension={!isFilteredReport ? 'sessions' : ''}
                  successMetricDisplayName={
                    metricNameMapper[dataConfig.selectedMetric] ||
                    dataConfig.selectedMetric
                  }
                  loading={graphDataLoading}
                  error={!!graphDataError}
                  title={`Links${
                    !isFilteredReport ? 'by creation source' : ''
                  }`}
                >
                  {graphDataLoading ? (
                    <></>
                  ) : (
                    <h1 className={styles.graphTitle}>
                      <span>
                        {metricNameMapper[dataConfig.selectedMetric] ||
                          dataConfig.selectedMetric}
                        {dateRangeLabel}
                        {!isFilteredReport ? ' split by creation source' : ''}
                      </span>
                    </h1>
                  )}
                </LostLinksGraph>
              </div>
              <div className={styles.tableWrapper}>
                <div className={styles.tableControls}>
                  <LostLinksSearchBox
                    dataConfig={dataConfig}
                    totalRows={totalTableRows}
                    loading={!tableData || tableDataLoading}
                    error={!!tableDataError}
                    searchFields={[
                      { name: 'All', value: 'all' },
                      { name: 'Landing page', value: 'landingPage' },
                      ...availableDimensions.map((dim) => ({
                        name: dim.dimensionName,
                        value: dim.dimensionParameterID,
                      })),
                    ]}
                    refetchData={(data) => {
                      setDataConfig(data)

                      refetchData(data)
                    }}
                  />
                  <Input
                    type="checkbox"
                    className={styles.groupByLandingPageCheckbox}
                    name="groupByLandingPage"
                    label="Group by landing page"
                    checked={groupTableDataByLandingPage}
                    onChange={async () => {
                      setGroupTableDataByLandingPage((curr) => !curr)

                      await getTableData({
                        variables: {
                          ...dataConfig,
                          ...initialPaginationConfig,
                          groupByLandingPage: !groupTableDataByLandingPage,
                          tempTable: undefined,
                        },
                      })
                    }}
                  />
                </div>
                <LostLinksTable
                  data={tableData}
                  loading={!tableData || tableDataLoading}
                  error={!!tableDataError}
                  totalRows={totalTableRows}
                  paginationData={paginationData}
                  setPaginationData={setPaginationData}
                  refetchData={(pagination) =>
                    refetchData(dataConfig, {
                      pagination: {
                        ...initialPaginationConfig,
                        ...pagination,
                      },
                      refetchGraph: false,
                    })
                  }
                  showEmptyColumns={!isFilteredReport}
                />
              </div>
            </div>
          </div>
        </Layout>
      </SiteWrapper>
      {showSaveReportModal && (
        <SaveEditReportModal
          toggleActive={setShowSaveReportModal}
          loading={savingNewReport}
          currentReport={currentReport}
          reportToEdit={reportToEdit}
          setReportToEdit={setReportToEdit}
          onSuccess={async (newReport: SavedReport, isNew = true) => {
            if (isNew) {
              await saveReport({
                variables: newReport,
              })

              logAction({
                variables: {
                  action: 'saved-lost-link-reports-add-new',
                  extra: JSON.stringify(newReport),
                  websiteSection: 'report',
                  functionName: 'saveReport',
                  pagePath: window.location.pathname,
                },
              })
            } else {
              updateSavedReport({
                variables: newReport,
              })

              logAction({
                variables: {
                  action: 'saved-lost-link-reports-update-existing',
                  extra: JSON.stringify(newReport),
                  websiteSection: 'report',
                  functionName: 'updateSavedReport',
                  pagePath: window.location.pathname,
                },
              })
            }

            setShowSaveReportModal(false)
          }}
          getNewReportConfig={getNewReportConfig}
        />
      )}
      {showEditReportsModal && (
        <ManageSavedReportsModal
          toggleActive={setShowEditReportsModal}
          savedReports={savedReports}
          reportsOrder={userPreferredOrder}
          setReportToEdit={setReportToEdit}
          setShowSaveReportModal={setShowSaveReportModal}
          loading={loadingSavedReports}
        />
      )}
    </>
  )
}
